import { useQueryClient } from '@tanstack/react-query';

import {
    useClientController_CreateNewClient,
    useClientController_GetAllClientsWithLocations,
    useClientController_GetClientSummaries,
    useClientController_GetClients,
    useClientController_GetClientWithLocations,
    useClientController_updateClient,
} from '@/services/hooks';
import {
    ClientController_GetClientSummaries,
    ClientController_GetClients,
} from '@/services/services';
import type { ClientInfo, ClientWithLocationsInfo, CreateNewClientInput } from '@/services/types';

import {
    type DomainMutation,
    type DomainMutationCallbacks,
    ignoreBody,
    toMutationOptions,
    useMappedMutation,
} from '../shared/mutation';
import { type DomainQuery, useMappedQuery } from '../shared/query';
import { ME_QUERY_KEY } from '../users/meCache';

import type { ClientUpdate } from './admin.types';
import { type ClientSummary, toClientSummaries } from './clientSummary';

/** The admin endpoints return their body as is; nothing is reshaped. */
function body<T>(value: T): T {
    return value;
}

/** Every client, for the internal client list. */
export function useGetClients(): DomainQuery<ClientInfo[]> {
    return useMappedQuery(useClientController_GetClients(), body<ClientInfo[]>);
}

/** Every client with its counts and recency, for the internal clients table. */
export function useGetClientSummaries(): DomainQuery<ClientSummary[]> {
    return useMappedQuery(useClientController_GetClientSummaries(), toClientSummaries);
}

/** One client with its locations and pens. Disabled while `enabled` is false. */
export function useGetClientWithLocations(
    clientId: number,
    options?: { enabled?: boolean }
): DomainQuery<ClientWithLocationsInfo> {
    return useMappedQuery(
        useClientController_GetClientWithLocations(clientId, options),
        body<ClientWithLocationsInfo>
    );
}

/** Every client with its locations, for picking where a bioscope goes. */
export function useGetAllClientsWithLocations(): DomainQuery<ClientWithLocationsInfo[]> {
    return useMappedQuery(
        useClientController_GetAllClientsWithLocations(),
        body<ClientWithLocationsInfo[]>
    );
}

function newClientToApi(client: CreateNewClientInput) {
    return { requestBody: client };
}

function clientUpdateToApi({ clientId, changes }: ClientUpdate) {
    return { clientId, requestBody: changes };
}

export function useCreateClient(
    callbacks?: DomainMutationCallbacks
): DomainMutation<CreateNewClientInput, void> {
    return useMappedMutation(
        useClientController_CreateNewClient(toMutationOptions(callbacks, ignoreBody)),
        newClientToApi,
        ignoreBody
    );
}

/**
 * Renames or archives a client. Once it settles, the client list and the session (whose client
 * names and archive flags the navigation reads) are refetched as well.
 */
export function useUpdateClient(
    callbacks?: DomainMutationCallbacks
): DomainMutation<ClientUpdate, void> {
    const queryClient = useQueryClient();
    const { onSettled, ...rest } = callbacks ?? {};
    return useMappedMutation(
        useClientController_updateClient(
            toMutationOptions(
                {
                    ...rest,
                    onSettled: () => {
                        onSettled?.();
                        queryClient.invalidateQueries({
                            queryKey: [ClientController_GetClients.key],
                        });
                        queryClient.invalidateQueries({
                            queryKey: [ClientController_GetClientSummaries.key],
                        });
                        queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
                    },
                },
                ignoreBody
            )
        ),
        clientUpdateToApi,
        ignoreBody
    );
}
